import { BULLETHEAD_PICTURES, BULLETHEAD_SPRITES } from './data/sprites';
import { SPRITE_SYMBOLS, type Picture, type Sprite } from './data/spriteTypes';
import { fixed } from './format';

/**
 * The bullethead's sprites on the sanctuary card (M75, the user's word: each
 * beside its group, drawn to scale): the young, the adults and the
 * terminal-phase males, each sprite as long as its group's mean total length
 * that day. The young is drawn from its picture whole, sharp (the user's
 * word: "make it look like that one", "the juvenile now looks blurry"), the
 * others from their pixels.
 */

/**
 * CSS pixels a millimeter of fish: the three side by side in the card's 340
 * px (its content's width, its padding outside it), the terminal-phase
 * males' mean, 280 to 290 mm, at 112 to 116 px, the adults', 150 to 230, at
 * 60 to 92, and the young's, 48 to 130, at 19 to 52, together with their
 * gaps at most 284.0 px, in May (MEASURED, M84: seed 42's world every day of
 * its first year, and every day of ten years of seeds 1, 42 and 4242; M75's,
 * before the roi, 291, 242 to 244 and 114 to 154 mm); each caption wraps to
 * its sprite's width. Where the card is
 * narrower (a phone held sideways), the three shrink by the same share
 * (each figure's basis its width, shrinking in proportion to it), so they
 * stay to scale.
 */
export const SPRITE_PX_PER_MM = 0.4;

const GROUPS = [
  { key: 'young', picture: 'young', label: 'young' },
  { key: 'adults', sprite: 'adult', label: 'adults' },
  { key: 'terminal', sprite: 'terminal', label: 'terminal-phase males' },
] as const;

/** The three, each at its group's mean length, mm; none for a group with no fish. */
export function fishFigures(lengthMm: Readonly<Record<'young' | 'adults' | 'terminal', number>>): string {
  const figures = GROUPS.filter((g) => lengthMm[g.key] > 0).map((g) => {
    const mm = lengthMm[g.key];
    const px = fixed(mm * SPRITE_PX_PER_MM, 1);
    const [attribute, s] = 'picture' in g ? [`data-picture="${g.picture}"`, BULLETHEAD_PICTURES[g.picture]] : [`data-sprite="${g.sprite}"`, BULLETHEAD_SPRITES[g.sprite]];
    return `<figure style="flex: 0 1 ${px}px"><canvas ${attribute} data-px="${px}" width="${s.width}" height="${s.height}"></canvas>`
      + `<figcaption>${g.label}, ${fixed(mm / 10, 0)} cm</figcaption></figure>`;
  });
  return figures.length ? `<dd class="fishes">${figures.join('')}</dd>` : '';
}

/** A sprite's pixels, read once. */
const pixels = new Map<Sprite, ImageData>();
function imageOf(s: Sprite): ImageData {
  const known = pixels.get(s);
  if (known) return known;
  const image = new ImageData(s.width, s.height);
  const colors = s.palette.map((hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)));
  s.rows.forEach((row, y) => [...row].forEach((ch, x) => {
    if (ch === '.') return;
    image.data.set([...(colors[SPRITE_SYMBOLS.indexOf(ch)] as number[]), 255], (y * s.width + x) * 4);
  }));
  pixels.set(s, image);
  return image;
}

/** A picture's pixels, its runs read once: each its color's place in the palette and one, or 0 where clear. */
const decoded = new Map<Picture, Uint8Array>();
function labelsOf(p: Picture): Uint8Array {
  const known = decoded.get(p);
  if (known) return known;
  const labels = new Uint8Array(p.width * p.height);
  p.rows.forEach((row, y) => {
    let x = 0;
    for (const [, symbol, count] of row.matchAll(/(\D)(\d*)/g)) {
      const run = count ? Number(count) : 1;
      if (symbol !== '.') labels.fill(SPRITE_SYMBOLS.indexOf(symbol as string) + 1, y * p.width + x, y * p.width + x + run);
      x += run;
    }
  });
  decoded.set(p, labels);
  return labels;
}

/**
 * A picture at a screen's pixels: each takes the one picture pixel under its
 * middle, so its edges stay sharp and its colors the picture's own (its
 * blocks flat, `scripts/sprites.ts`). The last size drawn kept.
 */
const drawnAt = new Map<Picture, ImageData>();
function pictureAt(p: Picture, width: number, height: number): ImageData {
  const known = drawnAt.get(p);
  if (known?.width === width && known.height === height) return known;
  const labels = labelsOf(p);
  const colors = p.palette.map((hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)));
  const image = new ImageData(width, height);
  for (let y = 0; y < height; y++) {
    const row = Math.min(p.height - 1, Math.floor(((y + 0.5) * p.height) / height)) * p.width;
    for (let x = 0; x < width; x++) {
      const label = labels[row + Math.min(p.width - 1, Math.floor(((x + 0.5) * p.width) / width))] as number;
      if (label) image.data.set([...(colors[label - 1] as number[]), 255], (y * width + x) * 4);
    }
  }
  drawnAt.set(p, image);
  return image;
}

/**
 * Paint every sprite's and picture's canvas under a root. A sprite's pixels
 * stay square (`pixelated`) where a screen pixel or more falls to each at its
 * scale; where fewer do, the browser smooths it, so no pixel is dropped whole.
 * A picture is drawn at the screen's own pixels (its width at its scale times
 * the device's pixels a CSS pixel), each from the picture pixel under its
 * middle. Judged at their scale, not as laid out, so a day's redraw forces no
 * layout (M75's reviewer: reading it cost a second forced layout a redrawn
 * day, as review M14 had removed); where a narrow card shrinks the three, the
 * browser scales them down further, square.
 */
export function paintSprites(root: ParentNode, devicePixels = window.devicePixelRatio || 1): void {
  for (const canvas of root.querySelectorAll<HTMLCanvasElement>('canvas[data-sprite]')) {
    const s = BULLETHEAD_SPRITES[canvas.dataset.sprite as 'adult' | 'terminal'];
    if (!s) continue;
    canvas.getContext('2d')?.putImageData(imageOf(s), 0, 0);
    canvas.style.imageRendering = Number(canvas.dataset.px) * devicePixels >= s.width ? 'pixelated' : 'auto';
  }
  for (const canvas of root.querySelectorAll<HTMLCanvasElement>('canvas[data-picture]')) {
    const p = BULLETHEAD_PICTURES[canvas.dataset.picture as 'young'];
    if (!p) continue;
    canvas.width = Math.max(1, Math.round(Number(canvas.dataset.px) * devicePixels));
    canvas.height = Math.max(1, Math.round((canvas.width * p.height) / p.width));
    canvas.getContext('2d')?.putImageData(pictureAt(p, canvas.width, canvas.height), 0, 0);
    canvas.style.imageRendering = 'pixelated';
  }
}
